import { useCallback, useMemo, useRef, useState } from 'react';
import {
  buildWebsiteRegionFormSchema,
  draftSaveObservedSlots,
  draftSaveOverlay,
  initialWebsiteRegionFormValues,
  websiteSlotCommitMode,
  websiteSlotValuesEqual,
  type DraftSaveQueue,
  type DraftSaveSnapshot,
  type StoreWebsiteDocument,
  type WebsiteEditRegion,
  type WebsiteRegionFieldState,
  type WebsiteRegionFormValues,
  type WebsiteSlotSchema,
  type WebsiteSlotValue,
  type WebsiteTemplateManifest,
} from '@dukanti/shared';
import { regionFieldIssueKey, rejectedFieldIssueKey } from '../lib/issue-messages';

export interface UseRegionEditorArgs {
  readonly manifest: WebsiteTemplateManifest;
  readonly document: StoreWebsiteDocument;
  readonly region: WebsiteEditRegion;
  /** Epic #228 P4: the template's auto-save queue; every valid edit goes into it. */
  readonly queue: DraftSaveQueue;
  readonly snapshot: DraftSaveSnapshot;
}

export interface RegionEditor {
  readonly region: WebsiteEditRegion;
  /** The schemas of the fields of the region, in manifest order. */
  readonly slots: readonly WebsiteSlotSchema[];
  /** What the dialog opened with (the document under the queue's newer values). Frozen while open. */
  readonly initial: WebsiteRegionFormValues;
  readonly values: WebsiteRegionFormValues;
  readonly setField: (slotId: string, state: WebsiteRegionFieldState) => void;
  /** Slot id → Admin locale key of the message under the field. */
  readonly fieldIssues: Readonly<Record<string, string>>;
  /**
   * Something only this dialog holds: a touched field the schema refuses
   * (never enqueued), a file on its way, or an unapplied crop (P6). Closing
   * would lose it, so the dialog asks first. Valid edits are in the queue and need no question.
   */
  readonly hasLocalUnsaved: boolean;
  /** A file of one of the fields is uploading. */
  readonly isUploading: boolean;
  /** Epic #228 P6: a field holds local work that is not a value yet (an unapplied crop). */
  readonly hasLocalWork: boolean;
  readonly setUploading: (slotId: string, uploading: boolean) => void;
  /**
   * Epic #228 P6: a field holds work that is only in the dialog and is not a
   * value yet (an unapplied crop). `null` ends it. It counts as local unsaved
   * work, so closing the dialog asks first.
   */
  readonly setLocalWork: (slotId: string, work: RegionLocalWork | null) => void;
  /**
   * Epic #228 P6: Escape while a field holds local work cancels that work
   * instead of closing the dialog (task A2). True when there was work to
   * cancel; the next Escape then reaches the close guard.
   */
  readonly cancelLocalWork: () => boolean;
  /** The dialog is closing: send what is pending now (fire and forget). */
  readonly close: () => void;
}

/** Epic #228 P6: what a field with local work tells the dialog. */
export interface RegionLocalWork {
  /** Drops the local work (the stored value is not touched). */
  readonly cancel: () => void;
}

/** The stored form of a field state: `null` is "use the template default again". */
function toBatchValue(state: WebsiteRegionFieldState | undefined): WebsiteSlotValue | null {
  if (state === undefined || state.mode === 'default') return null;
  if (state.mode === 'empty') return { empty: true };
  return state.value;
}

/**
 * The state of one region dialog (A21, epic #228 P4): the values of its
 * fields, what they are checked against, and the auto-save of each valid
 * edit.
 *
 * - The form is plain state, not React Hook Form: slot ids contain dots, which
 *   RHF reads as nested paths. Validation is the shared region schema.
 * - RULE 0 (design §5.1): `setField` validates the field; only a value the
 *   schema accepts is enqueued, with the commit mode of PRD §11.2 (typing is
 *   debounced, a choice is sent at once). An invalid value stays local with
 *   its message and is never sent.
 * - PD5: the dialog opens on the document overlaid with the queue's pending,
 *   in-flight and last-saved values. A slot's rebase base (`seen`) is what
 *   the server holds as far as this client knows: the live document under
 *   the queue's confirmed saves, read at the moment of the edit.
 * - Server outcomes (failed, conflict, a rejected slot) are the queue's; the
 *   dialog reads them from the snapshot.
 */
export function useRegionEditor({
  manifest,
  document,
  region,
  queue,
  snapshot,
}: UseRegionEditorArgs): RegionEditor {
  const schema = useMemo(() => buildWebsiteRegionFormSchema(manifest, region), [manifest, region]);
  const slots = useMemo(() => {
    const byId = new Map(manifest.slots.map((slot) => [slot.id, slot]));
    return region.slotIds.flatMap((slotId) => {
      const slot = byId.get(slotId);
      return slot ? [slot] : [];
    });
  }, [manifest, region]);
  const slotById = useMemo(() => new Map(slots.map((slot) => [slot.id, slot])), [slots]);

  // Frozen for as long as the dialog is open: the diff and the rebase base
  // are against what the merchant saw, not against a later refetch.
  const [initial] = useState(() =>
    initialWebsiteRegionFormValues(
      manifest,
      { ...document, slots: draftSaveOverlay(snapshot, document.slots) },
      region,
    ),
  );
  const [values, setValues] = useState<WebsiteRegionFormValues>(initial);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  // The latest server-confirmed document (not frozen): `seen` of a first touch.
  const observed = useMemo(
    () =>
      initialWebsiteRegionFormValues(
        manifest,
        { ...document, slots: draftSaveObservedSlots(snapshot, document.slots) },
        region,
      ),
    [document, manifest, region, snapshot],
  );
  const observedRef = useRef(observed);
  observedRef.current = observed;
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [uploadingSlots, setUploadingSlots] = useState<ReadonlySet<string>>(new Set());
  const localWorkRef = useRef<ReadonlyMap<string, RegionLocalWork>>(new Map());
  const [localWorkSlots, setLocalWorkSlots] = useState<ReadonlySet<string>>(new Set());

  const setField = useCallback(
    (slotId: string, state: WebsiteRegionFieldState): void => {
      const previous = valuesRef.current[slotId];
      const next: WebsiteRegionFormValues = { ...valuesRef.current, [slotId]: state };
      valuesRef.current = next;
      setValues(next);
      setTouched((current) => (current.has(slotId) ? current : new Set(current).add(slotId)));

      const slot = slotById.get(slotId);
      if (!slot) return;
      const parsed = schema.safeParse(next);
      const invalid =
        !parsed.success && parsed.error.issues.some((issue) => issue.path[0] === slotId);
      if (invalid) return;

      const before = toBatchValue(previous);
      const after = toBatchValue(state);
      if (websiteSlotValuesEqual(before, after)) return;
      queue.enqueue({ [slotId]: after }, websiteSlotCommitMode(slot, before, after), {
        [slotId]: toBatchValue(observedRef.current[slotId]),
      });
    },
    [queue, schema, slotById],
  );

  const setUploading = useCallback((slotId: string, uploading: boolean): void => {
    setUploadingSlots((current) => {
      if (current.has(slotId) === uploading) return current;
      const next = new Set(current);
      if (uploading) next.add(slotId);
      else next.delete(slotId);
      return next;
    });
  }, []);

  const setLocalWork = useCallback((slotId: string, work: RegionLocalWork | null): void => {
    const next = new Map(localWorkRef.current);
    if (work) next.set(slotId, work);
    else next.delete(slotId);
    localWorkRef.current = next;
    setLocalWorkSlots((current) => {
      if (current.has(slotId) === (work !== null)) return current;
      const slotsWithWork = new Set(current);
      if (work) slotsWithWork.add(slotId);
      else slotsWithWork.delete(slotId);
      return slotsWithWork;
    });
  }, []);

  const cancelLocalWork = useCallback((): boolean => {
    const works = [...localWorkRef.current.values()];
    for (const work of works) work.cancel();
    return works.length > 0;
  }, []);

  const parsed = useMemo(() => schema.safeParse(values), [schema, values]);
  const schemaIssues = useMemo(() => {
    const issues: Record<string, string> = {};
    if (parsed.success) return issues;
    for (const issue of parsed.error.issues) {
      const slotId = issue.path[0];
      // Only a field the merchant touched says anything: an untouched one
      // opened as it is and stays quiet.
      if (typeof slotId !== 'string' || !touched.has(slotId)) continue;
      const slot = slotById.get(slotId);
      const code = (issue as { params?: { code?: unknown } }).params?.code;
      issues[slotId] =
        slot && typeof code === 'string'
          ? regionFieldIssueKey(slot, values[slotId], code, issue.message)
          : issue.message;
    }
    return issues;
  }, [parsed, slotById, touched, values]);

  const fieldIssues = useMemo(() => {
    const issues: Record<string, string> = {};
    for (const slotId of Object.keys(snapshot.rejected)) {
      const rejection = snapshot.rejected[slotId];
      if (rejection && slotById.has(slotId)) issues[slotId] = rejectedFieldIssueKey(rejection.code);
    }
    return { ...issues, ...schemaIssues };
  }, [schemaIssues, slotById, snapshot.rejected]);

  const close = useCallback((): void => {
    void queue.flush();
  }, [queue]);

  return {
    region,
    slots,
    initial,
    values,
    setField,
    fieldIssues,
    hasLocalUnsaved:
      Object.keys(schemaIssues).length > 0 || uploadingSlots.size > 0 || localWorkSlots.size > 0,
    isUploading: uploadingSlots.size > 0,
    hasLocalWork: localWorkSlots.size > 0,
    setUploading,
    setLocalWork,
    cancelLocalWork,
    close,
  };
}
